import { getContentConfig } from '../api/requests/player.requests.js';
import { StatisticsStorage } from '../storage/statistics_storage.js';
import { PersistentCache } from '../offline/persistent_cache.js';
import { isConfigMissing } from '../utils/network_errors.js';
import {
  DEFAULT_INTERSTITIAL_CONFIG,
  interstitialLevelsPassed,
  isInterstitialDue,
  normalizeInterstitialRanges,
  type AdsInterstitialConfig,
} from './interstitial_frequency.js';

const ADS_CONFIG_CACHE_KEY = 'ads_config_v1';

export type AdsBannerConfig = {
  enabled: boolean;
  min_level: number;
  initial_delay_seconds: number;
  refresh_interval_seconds: number;
};

export type AdsRewardedConfig = {
  enabled: boolean;
  min_level_hearts: number;
  min_level_x2: number;
};

export type AdsRewardedBreakConfig = {
  enabled: boolean;
  firework_countdown: number;
  tenballs_countdown: number;
  infiniteballs_countdown: number;
  bigball_countdown: number;
  hearts_countdown: number;
  mysterybox_countdown: number;
};

export type AdsConfig = {
  banner: AdsBannerConfig;
  inter: AdsInterstitialConfig;
  rewarded: AdsRewardedConfig;
  rewarded_break: AdsRewardedBreakConfig;
};

const DEFAULT_ADS_CONFIG: AdsConfig = {
  banner: {
    enabled: false,
    min_level: 0,
    initial_delay_seconds: 3,
    refresh_interval_seconds: 30,
  },
  inter: DEFAULT_INTERSTITIAL_CONFIG,
  rewarded: {
    enabled: true,
    min_level_hearts: 1,
    min_level_x2: 1,
  },
  rewarded_break: {
    enabled: true,
    firework_countdown: 60,
    tenballs_countdown: 60,
    infiniteballs_countdown: 60,
    bigball_countdown: 60,
    hearts_countdown: 60,
    mysterybox_countdown: 60,
  },
};

function asRecord( value: unknown ): Record<string, unknown> | null
{
  return value != null && typeof value === 'object' && !Array.isArray( value )
    ? value as Record<string, unknown>
    : null;
}

function numberValue( value: unknown, fallback: number, min_value = 0 ): number
{
  const n = typeof value === 'number' ? value : Number( value );
  if ( !Number.isFinite( n ) ) return fallback;
  return Math.max( min_value, Math.floor( n ) );
}

function boolValue( value: unknown, fallback: boolean ): boolean
{
  if ( typeof value === 'boolean' ) return value;
  if ( typeof value === 'string' )
  {
    const normalized = value.trim().toLowerCase();
    if ( normalized === 'true' ) return true;
    if ( normalized === 'false' ) return false;
  }
  return fallback;
}

function normalizeAdsConfig( raw: unknown ): AdsConfig
{
  const root = asRecord( raw ) ?? {};
  const banner = asRecord( root.banner ) ?? {};
  const legacy_video_ads = asRecord( root.video_ads ) ?? {};
  const inter = asRecord( root.inter ) ?? asRecord( legacy_video_ads.inter ) ?? {};
  const rewarded = asRecord( root.rewarded ) ?? asRecord( root.rv ) ?? asRecord( legacy_video_ads.rv ) ?? {};
  const rewarded_break = asRecord( root.rewarded_break ) ?? {};

  return {
    banner: {
      enabled: boolValue( banner.enabled, DEFAULT_ADS_CONFIG.banner.enabled ),
      min_level: numberValue( banner.min_level, DEFAULT_ADS_CONFIG.banner.min_level ),
      initial_delay_seconds: numberValue(
        banner.initial_delay_seconds,
        DEFAULT_ADS_CONFIG.banner.initial_delay_seconds
      ),
      refresh_interval_seconds: numberValue(
        banner.refresh_interval_seconds,
        DEFAULT_ADS_CONFIG.banner.refresh_interval_seconds,
        1
      ),
    },
    inter: {
      enabled: boolValue( inter.enabled, DEFAULT_ADS_CONFIG.inter.enabled ),
      ranges: normalizeInterstitialRanges( inter ),
    },
    rewarded: {
      enabled: boolValue( rewarded.enabled, DEFAULT_ADS_CONFIG.rewarded.enabled ),
      min_level_hearts: numberValue(
        rewarded.min_level_hearts ?? rewarded.min_level ?? rewarded.min_games,
        DEFAULT_ADS_CONFIG.rewarded.min_level_hearts
      ),
      min_level_x2: numberValue(
        rewarded.min_level_x2 ?? rewarded.min_level ?? rewarded.min_games,
        DEFAULT_ADS_CONFIG.rewarded.min_level_x2
      ),
    },
    rewarded_break: {
      enabled: boolValue(
        rewarded_break.enabled,
        DEFAULT_ADS_CONFIG.rewarded_break.enabled
      ),
      firework_countdown: numberValue(
        rewarded_break.firework_countdown ?? rewarded_break.break_time,
        DEFAULT_ADS_CONFIG.rewarded_break.firework_countdown
      ),
      tenballs_countdown: numberValue(
        rewarded_break.tenballs_countdown ?? rewarded_break.break_time,
        DEFAULT_ADS_CONFIG.rewarded_break.tenballs_countdown
      ),
      infiniteballs_countdown: numberValue(
        rewarded_break.infiniteballs_countdown ?? rewarded_break.break_time,
        DEFAULT_ADS_CONFIG.rewarded_break.infiniteballs_countdown
      ),
      bigball_countdown: numberValue(
        rewarded_break.bigball_countdown
          ?? rewarded_break.pigball_countdown
          ?? rewarded_break.break_time,
        DEFAULT_ADS_CONFIG.rewarded_break.bigball_countdown
      ),
      hearts_countdown: numberValue(
        rewarded_break.hearts_countdown ?? rewarded_break.break_time,
        DEFAULT_ADS_CONFIG.rewarded_break.hearts_countdown
      ),
      mysterybox_countdown: numberValue(
        rewarded_break.mysterybox_countdown ?? rewarded_break.break_time,
        DEFAULT_ADS_CONFIG.rewarded_break.mysterybox_countdown
      ),
    },
  };
}

export class AdsConfigService
{
  private static config: AdsConfig = AdsConfigService.readCached() ?? DEFAULT_ADS_CONFIG;
  private static loaded = AdsConfigService.config !== DEFAULT_ADS_CONFIG;

  static hasCachedConfig(): boolean
  {
    return PersistentCache.read<unknown>( ADS_CONFIG_CACHE_KEY ) !== null;
  }
  private static loading: Promise<AdsConfig> | null = null;

  static get(): AdsConfig
  {
    return this.config;
  }

  static isLoaded(): boolean
  {
    return this.loaded;
  }

  static async refresh(): Promise<AdsConfig>
  {
    if ( this.loading ) return this.loading;

    this.loading = ( async () =>
    {
      try
      {
        const server_config = await getContentConfig<unknown>( 'ads' );
        this.config = normalizeAdsConfig( server_config );
        this.loaded = true;
        PersistentCache.write( ADS_CONFIG_CACHE_KEY, server_config );
        console.log( '[AdsConfig] loaded', this.config );
      }
      catch ( e )
      {
        if ( !this.loaded || isConfigMissing( e ) )
        {
          this.config = DEFAULT_ADS_CONFIG;
          this.loaded = false;
          PersistentCache.remove( ADS_CONFIG_CACHE_KEY );
        }
        console.warn( '[AdsConfig] using defaults', e );
        console.log( '[AdsConfig] active config', this.config );
      }
      finally
      {
        this.loading = null;
      }

      return this.config;
    } )();

    return this.loading;
  }

  private static readCached(): AdsConfig | null
  {
    const raw = PersistentCache.read<unknown>( ADS_CONFIG_CACHE_KEY );
    return raw == null ? null : normalizeAdsConfig( raw );
  }

  static getCurrentLevel(): number
  {
    return Math.max( 1, Math.floor( StatisticsStorage.load().level ) );
  }

  static isBannerAllowed( level = this.getCurrentLevel() ): boolean
  {
    const cfg = this.config.banner;
    return cfg.enabled && level >= cfg.min_level;
  }

  static isInterstitialDue( level = this.getCurrentLevel(), levels_passed = interstitialLevelsPassed() ): boolean
  {
    return isInterstitialDue( this.config.inter, level, levels_passed );
  }

  static isRewardedAllowed( level = this.getCurrentLevel() ): boolean
  {
    const cfg = this.config.rewarded;
    return cfg.enabled && level >= Math.min( cfg.min_level_hearts, cfg.min_level_x2 );
  }

  static isRewardedHeartsAllowed( level = this.getCurrentLevel() ): boolean
  {
    const cfg = this.config.rewarded;
    return cfg.enabled && level >= cfg.min_level_hearts;
  }

  static isRewardedX2Allowed( level = this.getCurrentLevel() ): boolean
  {
    const cfg = this.config.rewarded;
    return cfg.enabled && level >= cfg.min_level_x2;
  }

  static isRewardedPlacementAllowed( placement_id: string, level = this.getCurrentLevel() ): boolean
  {
    if ( placement_id === 'level_reward_x2' ) return this.isRewardedX2Allowed( level );
    if ( placement_id === 'rewarded' || placement_id === 'rewarded_heart' )
      return this.isRewardedHeartsAllowed( level );
    return this.isRewardedAllowed( level );
  }

}
